import { Dialog, Icon, Icons, Intent, Size, Spinner } from "construct-ui";
import moment from "moment";
import { SITE_STATUS, formats } from "../../config/constants";
import SiteManagementModel from "../../models/site-management/site-management.model";
import ViewBase from "../view-base.view";
import SiteManagementAttachmentUpdateDialogView from "./site-management-attachment-update-dialog.view";
import SiteManagementContactUpdateDialogView from "./site-management-contact-update-dialog.view";

/**
 * Site Management - Site Information Dialog View
 * 
 * A dialog page that displays detailed information, and allows users 
 * to manage contacts & attachments of a selected site
 * 
 * @author CV Leong
 */
export default function SiteManagementInformationDialogView() {
  ViewBase.call(this);

  let attachmentData;
  let contactData;
  let currentSiteId;
  let isDownloadingAllAttachments = false;
  let isShowingAttachmentUpdateDialog = false;
  let isShowingContactUpdateDialog = false;
  let tabDataGeneralInfo = {};
  let tabDataSoldToList = [];
  let tabDataProductUDFList = [];
  let tabDataContactInfo = [];
  let tabDataAttachmentList = [];

  const attachmentDownloadInProgress = new Map();

  const rowNo = (index) => index + 1;

  const getSiteGeneralInfo = (siteId) => {
    this.showLoading();
    SiteManagementModel.getSiteGeneralInfo({ siteId: siteId })
      .then(result => {
        tabDataGeneralInfo = { ...result };
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const getSiteSoldToList = (siteId) => {
    this.showLoading();
    SiteManagementModel.getSiteSoldToList({ siteId: siteId })
      .then(result => {
        tabDataSoldToList.splice(0);
        result?.map((col) => {
          tabDataSoldToList.push({ ...col })
        })
      })
      .catch((error) => {
        tabDataSoldToList = [];
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const getSiteProductUDFs = (siteId) => {
    this.showLoading();
    SiteManagementModel.getSiteProductUDFs({ siteId: siteId })
      .then(result => {
        tabDataProductUDFList.splice(0);
        result?.map((col) => {
          tabDataProductUDFList.push({ ...col })
        })
      })
      .catch((error) => {
        tabDataProductUDFList = [];
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const getSiteContactInfo = (siteId) => {
    this.showLoading();
    SiteManagementModel.getSiteContactInfo({ siteId: siteId })
      .then(result => {
        tabDataContactInfo.splice(0);
        result?.map((col) => {
          tabDataContactInfo.push({ ...col })
        })
      })
      .catch((error) => {
        tabDataContactInfo = [];
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const removeSiteContactInfo = (siteId, contactId, userId) => {
    this.showConfirm(
      this.getText('LT00000878', 'Do you want to remove the contact?'),
      `${this.getText('LT00000877', 'Remove Contact')} - ${userId}`,
      (e) => {
        if (e === 'YES') {
          SiteManagementModel.removeSiteContactInfo({
            siteId: siteId,
            id: contactId,
          })
            .then(() => {
              // remove from table
              tabDataContactInfo = tabDataContactInfo.filter(contact => contact.contactId !== contactId);
            })
        }
      }
    )
  }

  const getSiteAttachmentList = (siteId) => {
    this.showLoading();
    SiteManagementModel.getSiteAttachmentList({ siteId: siteId })
      .then(result => {
        tabDataAttachmentList.splice(0);
        result?.map((col) => {
          tabDataAttachmentList.push({ ...col })
        })
      })
      .catch((error) => {
        tabDataAttachmentList = [];
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const loadSiteInformation = (siteId) => {
    // Tab 1 - General Information
    getSiteGeneralInfo(siteId);
    // Tab 2 - Sold To List
    getSiteSoldToList(siteId);
    // Tab 3 - Product UDF List
    getSiteProductUDFs(siteId);
    // Tab 4 - Contact Information
    getSiteContactInfo(siteId);
    // Tab 5 - Attachment List
    getSiteAttachmentList(siteId);
  }

  const getStatusClass = (statusCode) => {
    return SITE_STATUS.find((status) => status.code === statusCode)?.class
  }

  const openContactUpdateDialog = (siteId, data = null) => {
    currentSiteId = siteId;
    contactData = data;
    isShowingContactUpdateDialog = true;
  }

  const openAttachmentUpdateDialog = (siteId, data = null) => {
    currentSiteId = siteId;
    attachmentData = data;
    isShowingAttachmentUpdateDialog = true;
  }

  const downloadSiteAttachmentList = (siteId) => {
    isDownloadingAllAttachments = true;
    SiteManagementModel.downloadSiteAttachmentList({ siteId })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        isDownloadingAllAttachments = false;
      })
  }

  const downloadSiteAttachment = (siteId, attachment) => {
    if (attachmentDownloadInProgress.has(attachment.fileId)) {
      return false;
    }

    attachmentDownloadInProgress.set(attachment.fileId);

    const params = {
      siteId,
      fileId: attachment.fileId
    }

    SiteManagementModel.downloadSiteAttachmentByFileId(params, attachment.fileName)
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        attachmentDownloadInProgress.delete(attachment.fileId)
      })
  }

  const removeSiteAttachment = (siteId, attachment) => {
    this.showConfirm(
      this.getText('LT00000888', 'Do you want to remove the attachment?'),
      `${this.getText('LT00000889', 'Remove Attachment')} - ${attachment.title}`,
      (e) => {
        if (e === 'YES') {
          SiteManagementModel.removeSiteAttachment({
            siteId,
            fileId: attachment.fileId,
          })
            .then(() => {
              // remove from table
              tabDataAttachmentList = tabDataAttachmentList.filter(_attachment => _attachment.fileId !== attachment.fileId);
            })
            .catch((error) => {
              this.handleError(error)
            })
        }
      }
    )
  }

  const ContentBody = () => {
    return {
      view: ({ attrs: { siteId } }) => {
        return [
          m('.container',
            // Nav tabs
            m('nav',
              m('.nav.nav-tabs.flex-row.justify-content-start#nav-tabs[role="tablist"]',
                m('button.nav-item.nav-link.active#nav-tab-general-info[data-bs-toggle="tab"][data-bs-target="#nav-content-general-info"][aria-controls="nav-content-general-info"][type="button"][role="tab"][aria-selected="true"]',
                  this.getText('LT00000223', 'General Information')
                ),
                m('button.nav-item.nav-link#nav-tab-sold-to[data-bs-toggle="tab"][data-bs-target="#nav-content-sold-to"][aria-controls="nav-content-sold-to"][type="button"][role="tab"][aria-selected="true"]',
                  `${this.getText('LT00000835', 'Sold-To')} (${this.getText('LT00000873', 'Sell-In')})`
                ),
                m('button.nav-item.nav-link#nav-tab-site-product-udf[data-bs-toggle="tab"][data-bs-target="#nav-content-site-product-udf"][aria-controls="nav-content-site-product-udf"][type="button"][role="tab"][aria-selected="true"]',
                  this.getText('LT00000836', 'Site Product UDF')
                ),
                m('button.nav-item.nav-link#nav-tab-site-contact-info[data-bs-toggle="tab"][data-bs-target="#nav-content-contact-info"][aria-controls="nav-content-contact-info"][type="button"][role="tab"][aria-selected="true"]',
                  this.getText('LT00000837', 'Contact Information')
                ),
                m('button.nav-item.nav-link#nav-tab-attachments[data-bs-toggle="tab"][data-bs-target="#nav-content-attachments"][aria-controls="nav-content-attachments"][type="button"][role="tab"][aria-selected="true"]',
                  this.getText('LT00000047', 'Attachments')
                ),
              )
            ),
            // Tab content
            m('.tab-content#nav-tabContent',
              // Tab 1 - General Information
              m('.tab-pane.fade.show.active#nav-content-general-info[role="tabpanel"][aria-labelledby="nav-tab-general-info"]',
                m('.row.g-1',
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000144', 'Site Name')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.siteName || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000809', 'Site ID')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.siteId || '-')),
                    )
                  ),
                  m('.col-12',
                    m('.row.g-1',
                      m('.col-5.col-md-3.col-lg-2', this.getText('LT00000839', 'Site Description')),
                      m('.col-7.col-md-9.col-lg-10', m('span.font-weight-bold', tabDataGeneralInfo.siteDescription || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000015', 'Status')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('.badge.rounded-pill.site-status', { class: getStatusClass(tabDataGeneralInfo.siteStatusCode) }, tabDataGeneralInfo.siteStatus)),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000871', 'Incentive Agree')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.incentiveAgree || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000808', 'Site Type')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.siteTypeName || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000872', 'Site Category')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.siteCategoryName || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000838', 'Contact Person')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.contactPerson || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000247', 'Contact Number')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.contactNumber || '-')),
                    )
                  ),
                ),
                m('hr'),
                m('.row.g-1',
                  m('.col-12',
                    m('.row.g-1',
                      m('.col-5.col-md-3.col-lg-2', this.getText('LT00000557', 'Location')),
                      m('.col-7.col-md-9.col-lg-10', m('span.font-weight-bold', tabDataGeneralInfo.location || '-')),
                    )
                  ),
                  m('.col-12',
                    m('.row.g-1',
                      m('.col-5.col-md-3.col-lg-2', this.getText('LT00000216', 'Address')),
                      m('.col-7.col-md-9.col-lg-10', m('span.font-weight-bold', tabDataGeneralInfo.address || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000842', 'Postal Code')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.postalCode || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', `${this.getText('LT00000868', 'Site Size')} (m`, m('sup', '2'), ')'),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.shopSize || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000840', 'Latitude')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.latitude || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000841', 'Longitude')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.longitude || '-')),
                    )
                  ),
                ),
                m('hr'),
                m('.row.g-1',
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000233', 'Division')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.divisions || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000862', 'Product Group')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.productGroups || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000072', 'Site Group')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.siteGroup || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000863', 'Shipment Type')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.shipmentTypeName || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000864', 'Aggregation Type')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.aggregationTypeName || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000865', 'Coverage')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.coverage || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000866', 'Specialty')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.specialty || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000867', 'Exclusivity')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.exclusivity || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000869', 'GSCM Update User')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.gscmUpdateName || '-')),
                    )
                  ),
                  m('.col-12.col-md-6',
                    m('.row.g-1',
                      m('.col-5.col-sm-5.col-md-6.col-lg-4', this.getText('LT00000870', 'GSCM Update Date')),
                      m('.col-7.col-sm-7.col-md-6.col-lg-8', m('span.font-weight-bold', tabDataGeneralInfo.gscmUpdateDateTime || '-')),
                    )
                  ),
                ),
              ), // End of Tab 1 - General Information
              // Tab 2 - Sold To
              m('.tab-pane.fade.show#nav-content-sold-to[role="tabpanel"][aria-labelledby="nav-tab-sold-to"]',
                m('.table-responsive',
                  m('table.table.table-hover.sticky-column-start',
                    m('thead',
                      m('tr.align-middle',
                        m('th', this.getText('LT00000721', 'No.')),
                        m('th', this.getText('LT00000847', 'Sold-To Code')),
                        m('th', this.getText('LT00000848', 'Sold-To Name')),
                        m('th', this.getText('LT00000241', 'GSCM Division')),
                      )
                    ),
                    m('tbody',
                      (tabDataSoldToList.length === 0) &&
                      m('tr',
                        m('td[colspan=4]', this.getText("EM00000141", "No records found."))
                      ),
                      tabDataSoldToList.length > 0 &&
                      tabDataSoldToList.map((col, index) => [
                        m('tr.align-middle', { key: `${col.siteId}-${col.gscmDiv}` },
                          m('td', rowNo(index)),
                          m('td', col.shipToCode),
                          m('td', col.soldToName),
                          m('td', col.gscmDiv),
                        )
                      ]),
                    )
                  )
                )
              ), // End of Tab 2 - Sold To
              // Tab 3 - Site Product UDF
              m('.tab-pane.fade.show#nav-content-site-product-udf[role="tabpanel"][aria-labelledby="nav-tab-site-product-udf"]',
                tabDataProductUDFList.map((productGroup, index) => [
                  index > 0 &&
                  m('.row',
                    m('.col-12',
                      m('hr')
                    )
                  ),
                  m('.row.g-1',
                    m('.col-12',
                      m('.row.g-1',
                        m('.col-12',
                          m('h4', `${this.getText('LT00000862', 'Product Group')}:  ${productGroup.productGroupName}`)
                        )
                      )
                    ),
                    productGroup.attributes.map((udf) => [
                      m('.col-12.col-md-6',
                        m('.row.g-1',
                          m('.col-6.col-sm-5.col-md-6.col-lg-5', `${udf.name}: `),
                          m('.col-6.col-sm-7.col-md-6.col-lg-7', m('span.font-weight-bold', udf.udfValue || '-')),
                        )
                      ),
                    ])
                  )
                ])
              ), // End of Tab 3 - Site Product UDF
              // Tab 4 - Contact Info
              m('.tab-pane.fade.show#nav-content-contact-info[role="tabpanel"][aria-labelledby="nav-tab-contact-info"]',
                m('.row.mb-2',
                  m('.col-12.text-end',
                    m('a.btn.btn-outline-primary', {
                      onclick: () => {
                        openContactUpdateDialog(siteId);
                      }
                    },
                      m('i.bi.bi-plus-lg'),
                      this.getText('LT00000090', 'Add')
                    )
                  )
                ),
                m('.table-responsive',
                  m('table.table.table-hover.sticky-column-start',
                    m('thead',
                      m('tr.align-middle',
                        m('th', this.getText('LT00000721', 'No.')),
                        m('th', this.getText('LT00000874', 'Product Hierarchy')),
                        m('th', this.getText('LT00000579', 'Product')),
                        m('th', this.getText('LT00000876', 'Contact User ID')),
                        m('th', this.getText('LT00000838', 'Contact Person')),
                        m('th', this.getText('LT00000875', 'Relation')),
                        m('th', this.getText('LT00000247', 'Contact Number')),
                        m('th', this.getText('LT00000152', 'Remarks')),
                        m('th.text-center', this.getText('LT00000033', 'Actions')),
                      )
                    ),
                    m('tbody',
                      (tabDataContactInfo.length === 0) &&
                      m('tr',
                        m('td[colspan=9]', this.getText("EM00000141", "No records found."))
                      ),
                      tabDataContactInfo.length > 0 &&
                      tabDataContactInfo.map((col, index) => [
                        m('tr.align-middle', { key: col.contactId },
                          m('td', rowNo(index)),
                          m('td', col.productHierarchy),
                          m('td', col.product),
                          m('td', col.contactUserId),
                          m('td', col.contactName),
                          m('td', col.relation),
                          m('td', col.contactNumber),
                          m('td', col.remark),
                          m('td',
                            m('.d-flex.gap-2.justify-content-center',
                              m(Icon, {
                                intent: Intent.PRIMARY,
                                name: Icons.EDIT,
                                onclick: () => {
                                  openContactUpdateDialog(siteId, col);
                                },
                                title: this.getText('LT00000128', 'Edit'),
                              }),
                              m(Icon, {
                                intent: Intent.NEGATIVE,
                                name: Icons.TRASH_2,
                                onclick: () => {
                                  removeSiteContactInfo(siteId, col.contactId, col.contactUserId);
                                },
                                title: this.getText('LT00000135', 'Delete'),
                              }),
                            )
                          ),
                        )
                      ]),
                    )
                  )
                )
              ), // End of Tab 4 - Contact Info
              // Tab 5 - Attachments
              m('.tab-pane.fade.show#nav-content-attachments[role="tabpanel"][aria-labelledby="nav-tab-attachments"]',
                m('.row.mb-2',
                  m('.col-12.d-flex.justify-content-end.gap-2',
                    m('a.btn.btn-outline-primary', {
                      class: isDownloadingAllAttachments ? 'disabled' : '',
                      onclick: () => { downloadSiteAttachmentList(siteId) }
                    },
                      isDownloadingAllAttachments
                        ? m('span.spinner-border.spinner-border-sm.me-2[role=status][aria-hidden=true]')
                        : m('i.bi.bi-file-earmark-arrow-down'),
                      this.getText('LT00000881', 'Download All')
                    ),
                    m('a.btn.btn-outline-primary', {
                      onclick: () => {
                        openAttachmentUpdateDialog(siteId);
                      }
                    },
                      m('i.bi.bi-file-earmark-arrow-up'),
                      this.getText('LT00000882', 'Upload File')
                    )
                  )
                ),
                m('.table-responsive',
                  m('table.table.table-hover.sticky-column-start',
                    m('thead',
                      m('tr.align-middle',
                        m('th', this.getText('LT00000721', 'No.')),
                        m('th', this.getText('LT00000037', 'Title')),
                        m('th', this.getText('LT00000883', 'File Name')),
                        m('th', this.getText('LT00000884', 'File Size')),
                        m('th', this.getText('LT00000710', 'Updated By')),
                        m('th', this.getText('LT00000887', 'Updated On')),
                        m('th', this.getText('LT00000475', 'Update Reason')),
                        m('th.text-center', this.getText('LT00000033', 'Actions')),
                      )
                    ),
                    m('tbody',
                      (tabDataAttachmentList.length === 0) &&
                      m('tr',
                        m('td[colspan=9]', this.getText("EM00000141", "No records found."))
                      ),
                      tabDataAttachmentList.length > 0 &&
                      tabDataAttachmentList.map((col, index) => [
                        m('tr.align-middle', { key: col.fileId },
                          m('td', rowNo(index)),
                          m('td', col.title),
                          m('td', col.fileName),
                          m('td', `${col.fileSize} KB`),
                          m('td', col.createUserId),
                          m('td', moment(col.createDateTime, 'MM/DD/YYYY hh:mm:ss').format(formats.dateFormat)),
                          m('td', col.reason),
                          m('td',
                            m('.d-flex.gap-2.justify-content-center.align-items-center',
                              m('.position-relative',
                                m(Icon, {
                                  class: attachmentDownloadInProgress.has(col.fileId) ? 'opacity-0' : '',
                                  intent: Intent.PRIMARY,
                                  name: Icons.DOWNLOAD,
                                  onclick: () => { downloadSiteAttachment(siteId, col) },
                                  title: this.getText('LT00000492', 'Download'),
                                }),
                                attachmentDownloadInProgress.has(col.fileId) &&
                                m(Spinner, {
                                  active: true,
                                  fill: true,
                                  intent: Intent.PRIMARY,
                                  size: Size.XS,
                                })
                              ),
                              m('div',
                                m(Icon, {
                                  intent: Intent.PRIMARY,
                                  name: Icons.EDIT,
                                  onclick: () => { openAttachmentUpdateDialog(siteId, col) },
                                  title: this.getText('LT00000128', 'Edit'),
                                }),
                              ),
                              m('div',
                                m(Icon, {
                                  intent: Intent.NEGATIVE,
                                  name: Icons.TRASH_2,
                                  onclick: () => { removeSiteAttachment(siteId, col) },
                                  title: this.getText('LT00000135', 'Delete'),
                                }),
                              )
                            )
                          ),
                        )
                      ]),
                    )
                  )
                )
              ), // End of Tab 5 - Attachments
            ),
          ),
          this.getBaseViews()
        ]
      }
    }
  }

  return {
    view: ({ attrs: {
      isOpen,
      onClose,
      siteId
    } }) => {
      return [
        m(Dialog, {
          class: 'cui-dialog-xxl',
          title: `${this.getText('LT00000834', 'Site Information')}: ${siteId} - ${tabDataGeneralInfo.siteName}`,
          isOpen: isOpen,
          onClose: onClose,
          onOpened: () => { loadSiteInformation(siteId); },
          closeOnOutsideClick: false,
          content: m(ContentBody, { siteId: siteId }),
          footer: [
            m('.d-flex.justify-content-end.gap-2',
              m('button.btn.btn-white', {
                onclick: () => { onClose(false) },
              }, this.getText('LT00000021', 'Close')),
            )
          ],
        }),
        m(SiteManagementContactUpdateDialogView, {
          isOpen: isShowingContactUpdateDialog,
          data: contactData,
          siteId: currentSiteId,
          onClose: (requireRefresh) => {
            if (requireRefresh === true) {
              getSiteContactInfo(siteId)
            }
            isShowingContactUpdateDialog = false;
          },
        }),
        m(SiteManagementAttachmentUpdateDialogView, {
          isOpen: isShowingAttachmentUpdateDialog,
          data: attachmentData,
          siteId: currentSiteId,
          onClose: (requireRefresh) => {
            if (requireRefresh === true) {
              getSiteAttachmentList(siteId)
            }
            isShowingAttachmentUpdateDialog = false;
          },
        }),
      ];
    }
  };
}
